import { Button } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

/**
 * How far back the portfolio chart looks.
 *
 * ==========================================================================================
 * THREE RANGES, NOT A SLIDER
 * ==========================================================================================
 *
 * A household asks one of three questions about a portfolio — how is it doing this year, how has it
 * done since we started, and what does a decade look like — and each has an obvious answer in
 * months. A continuous control would make somebody choose 43 months, which answers nothing anybody
 * asked.
 *
 * The spec said "no range selector in v1". Overturned on feedback: investments is the one pillar
 * where the interesting horizon is often a decade, and the prices to draw it are already stored.
 */

export const RANGES = [
  { months: 12, label: "12 months" },
  { months: 60, label: "5 years" },
  { months: 120, label: "10 years" },
] as const;

export interface RangeControlsProps {
  months: number;
  onChange: (months: number) => void;
}

export function RangeControls({ months, onChange }: RangeControlsProps): ReactNode {
  const t = useT();
  return (
    <div className="flex gap-2" role="group" aria-label={t("inv.chart.range")}>
      {RANGES.map((range) => (
        <Button
          key={range.months}
          variant={range.months === months ? "primary" : "outline"}
          aria-pressed={range.months === months}
          onClick={() => {
            onChange(range.months);
          }}
        >
          {range.label}
        </Button>
      ))}
    </div>
  );
}
